import React from "react";
import { money } from "@/lib/money";
import { distanceLabel } from "@/lib/distance";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button, Card, Chip, DanyProviderResults, EmptyState, Icon, JobCard,
  StatusBadge,
} from "@/ds";
import api, { mediaUrl } from "@/lib/api";
import { categoryLabel } from "@/lib/catalog";
import { budgetText, jobLocationText } from "@/lib/jobDisplay";
import { jobDetailPath } from "@/lib/ownerRoutes";
import { useAuth } from "@/contexts/AuthContext";
import DanyJobDraftWidget from "@/components/dany/DanyJobDraftWidget";
import DanyQuoteRequestWidget from "@/components/dany/DanyQuoteRequestWidget";
import DanyProfileWidget from "@/components/dany/DanyProfileWidget";
import DanyJobPhotosWidget from "@/components/dany/DanyJobPhotosWidget";
import DanyQuoteWidget from "@/components/dany/DanyQuoteWidget";
import DanyProviderSetupWidget from "@/components/dany/DanyProviderSetupWidget";
import DanyMarketPriceWidget from "@/components/dany/DanyMarketPriceWidget";
import DanyTicketWidget from "@/components/dany/DanyTicketWidget";

/**
 * The widgets Dany renders inside a turn, keyed by the tool that produced them.
 *
 * "La conversación invoca la UI, no la reemplaza" (DANY.md §4). Every widget is
 * built from `tool_result.data` — the payload the backend deliberately ships
 * whole alongside the model-facing summary, and the same payload a reloaded
 * transcript carries in `tool_data`. That is why a widget looks identical live
 * and after a refresh: it never reads the prose.
 *
 * Widgets reuse the marketplace components verbatim. The chat never gets its own
 * card styling — a second ProviderCard would drift from the directory's the
 * first time someone tuned one.
 */

/**
 * Above 100 miles the number stops being information — a FixyBee 1,640 miles
 * away is a real row, because the search widens when nothing is nearby, but
 * "1640.9 mi" on the card reads as a bug rather than as a caveat. That rule
 * and the formatting both live in `lib/distance.js` now, the twin of
 * `services/distance.py` (N4), and the unit stopped depending on the interface
 * language on the way: this marketplace is in the United States whichever
 * language it is being read in.
 *
 * **Imported and then re-exported, not re-exported straight through** (N35).
 * `export { distanceLabel } from "@/lib/distance"` forwards the binding to
 * importers of this module without ever creating one in this module's own
 * scope, so `toProviderProps` below — which calls it — was reading a free
 * variable that did not exist. It threw `ReferenceError: distanceLabel is not
 * defined` the first time a `find_fixybees` result carried a provider, which
 * with no boundary above it unmounted the whole React tree: production
 * 2026-09-19, a white screen on every text turn that found somebody. The
 * re-export stays because three call sites already import it from here.
 */
export { distanceLabel };

/** Backend provider card -> ds ProviderCard props. */
function toProviderProps(t, language, provider) {
  return {
    id: provider.user_id,
    name: provider.full_name,
    company: provider.company_name || provider.full_name,
    city: provider.location_city,
    state: provider.location_state,
    avatarUrl: provider.avatar_url ? mediaUrl(provider.avatar_url) : undefined,
    verified: !!provider.is_verified,
    rating: provider.average_rating || 0,
    jobsCount: provider.completed_jobs_count || 0,
    bio: provider.bio,
    distance: distanceLabel(provider.distance_miles),
    ctaLabel: t("providers.visit"),
    verifiedLabel: t("providers.verified"),
    newLabel: t("providers.newHere"),
    jobsLabel: t("providers.jobsDone", { count: provider.completed_jobs_count || 0 }),
    onVisit: provider.public_slug
      ? () => window.open(`/pro/${provider.public_slug}`, "_blank", "noopener")
      : undefined,
  };
}

/**
 * The honest empty (DY1.4).
 *
 * The search is bounded by `search_radius_miles` now, so "nothing found" is a
 * real answer with a real number behind it instead of a hole the ranking used
 * to paper over with someone 1,600 miles away. Rendering nothing at all would
 * leave Dany's sentence alone on the page; rendering this turns the gap into
 * the only conversion available in an uncovered city — the visitor probably
 * knows the plumber we are missing.
 */
export function NoneNearby({ radiusMiles, zip }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const radius = Math.round(Number(radiusMiles) || 0);
  return (
    <EmptyState
      title={t("search.noneNearbyTitle")}
      body={zip
        ? t("search.noneNearbyBody", { radius, zip })
        : t("search.noneNearbyBodyNoZip", { radius })}
      primaryAction={{
        label: t("search.inviteBeeCta"),
        onClick: () => navigate("/become-a-fixybee"),
      }}
      secondaryAction={{
        label: t("search.browseAll"),
        onClick: () => navigate("/providers"),
      }}
    />
  );
}

function FixyBeeResults({ data }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const providers = data?.providers || [];
  // A bounded search that came back empty has a radius on it; a tool that
  // failed for some other reason does not, and that one still renders nothing.
  if (!providers.length) {
    return data?.radius_miles
      ? <NoneNearby radiusMiles={data.radius_miles} zip={data.zip_code} />
      : null;
  }
  // Three inline, the rest behind "see all" — a reading column is 720px wide and
  // a fourth card buys nothing (DanyProviderResults.prompt.md).
  const shown = providers.slice(0, 3);
  return (
    <DanyProviderResults
      caption={t("dany.widgets.fixybeesCaption", { count: providers.length })}
      providers={shown.map((p) => toProviderProps(t, i18n.language, p))}
      seeAllLabel={t("dany.widgets.seeAllFixybees")}
      // Carrying the category is what makes this "the rest of these" instead of
      // "every FixyBee in the country". The directory cannot reproduce the
      // distance ranking — it has no ZIP, and its own distance-ranked path is
      // the AI search at the top of that page — so the category is the part of
      // the search that survives the trip.
      onSeeAll={() => navigate(data?.category_id
        ? `/providers?category=${encodeURIComponent(data.category_id)}`
        : "/providers")}
    />
  );
}

/**
 * Categories, as chips Dany acts on.
 *
 * A chip in the thread is an action, not a link: tapping it asks Dany for
 * FixyBees in that category and the answer lands in this same conversation.
 * Navigating away to /providers would drop the visitor out of the thread they
 * are in the middle of — the landing's own category cards still navigate,
 * because there the grid IS the navigation.
 */
/** Chips shown before the row folds. Eight is roughly two lines at thread width. */
const CATEGORY_CHIP_LIMIT = 8;

function CategoryResults({ data, onAskCategory }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState(false);
  const all = data?.categories || [];
  if (!all.length) return null;
  const shown = expanded ? all : all.slice(0, CATEGORY_CHIP_LIMIT);
  const hidden = all.length - shown.length;
  return (
    <div>
      <p style={eyebrow}>
        <Icon name="list" size={12} style={{ color: "var(--hive-ink)" }} />
        {/* The TOTAL, not how many chips fit. Reporting the truncated count said
            "8 matching categories" when the catalog returned 32, which is both
            wrong and the reason Dany felt it had to recite the list itself. */}
        {t("dany.widgets.categoriesCaption", { count: all.length })}
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
        {shown.map((c) => {
          const label = categoryLabel(t, c);
          return (
            <Chip
              key={c.category_id}
              icon="search"
              title={t("dany.widgets.categoryChipHint", { category: label })}
              onClick={() => (
                onAskCategory
                  ? onAskCategory({ label, category_id: c.category_id })
                  : navigate(`/providers?category=${c.category_id}`)
              )}
            >
              {label}
            </Chip>
          );
        })}
        {hidden > 0 && (
          <Chip icon="plus" onClick={() => setExpanded(true)} data-testid="category-chips-more">
            {t("dany.widgets.categoriesMore", { count: hidden })}
          </Chip>
        )}
      </div>
    </div>
  );
}

function PlanResults({ data }) {
  const { t } = useTranslation();
  const plans = data?.plans || [];
  if (!plans.length) return null;
  return (
    <div>
      <p style={eyebrow}>
        <Icon name="credit-card" size={12} style={{ color: "var(--dusk)" }} />
        {t("dany.widgets.plansCaption")}
      </p>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
        gap: "var(--space-3)",
      }}>
        {plans.map((plan) => (
          <Card key={plan.plan_id} padding="sm">
            {/* Identical cards (PLANES M4a): no plan is badged as the one to
                pick — they differ only in the quotes line below. */}
            <p style={{
              fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
              fontSize: "var(--text-base)", color: "var(--text-body)",
            }}>{plan.name}</p>
            <p style={{
              marginTop: 6, fontFamily: "var(--font-mono)",
              fontSize: "var(--text-xl)", color: "var(--text-body)",
            }}>
              ${plan.price_monthly_usd}
              <span style={{ fontSize: "var(--text-xs)", color: "var(--text-subtle)" }}>
                {t("dany.widgets.perMonth")}
              </span>
            </p>
            <p style={{ marginTop: 4, fontSize: "var(--text-sm)", color: "var(--text-body)" }}>
              <span style={{ fontFamily: "var(--font-mono)", fontWeight: "var(--weight-bold)" }}>
                {plan.included_quotes_per_month}
              </span>{" "}
              {t("planCards.quotesLabel", { count: Number(plan.included_quotes_per_month) || 0 })}
            </p>
          </Card>
        ))}
      </div>
      <Link to="/pricing" style={seeAll}>
        {t("dany.widgets.seeAllPlans")}<Icon name="arrow-right" size={15} />
      </Link>
    </div>
  );
}

function JobResults({ data }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // The owner's own namespace (DY2.6): `my_jobs` is offered to a FixyBee now,
  // and a card of theirs pointing at `/client/jobs/...` would bounce off
  // `ProtectedRoute` into their dashboard.
  const { user } = useAuth();
  const jobs = (data?.jobs || []).slice(0, 3);
  if (!jobs.length) return null;
  return (
    <div>
      <p style={eyebrow}>
        <Icon name="briefcase" size={12} style={{ color: "var(--hive-ink)" }} />
        {t("dany.widgets.jobsCaption", { count: jobs.length })}
      </p>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
        gap: "var(--space-3)",
      }}>
        {jobs.map((job) => (
          <JobCard
            key={job.job_id}
            title={job.title}
            category={job.category_id}
            city={job.location?.city}
            state={job.location?.state}
            locationLabel={jobLocationText(job)}
            budgetLabel={budgetText(t, job.budget_min, job.budget_max)}
            quotesCount={job.quotes_count}
            status={job.status}
            ctaLabel={t("dany.widgets.openJob")}
            onAction={() => navigate(jobDetailPath(user?.profile?.role, job.job_id))}
          />
        ))}
      </div>
    </div>
  );
}

function QuoteResults({ data }) {
  const { t } = useTranslation();
  const quotes = (data?.quotes || []).slice(0, 4);
  if (!quotes.length) return null;
  return (
    <div>
      <p style={eyebrow}>
        <Icon name="file-text" size={12} style={{ color: "var(--hive-ink)" }} />
        {t("dany.widgets.quotesCaption", { count: quotes.length })}
      </p>
      <div style={{
        display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "var(--space-2)",
      }}>
        {quotes.map((quote) => (
          <Card key={quote.quote_id} padding="sm">
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: "var(--space-3)", flexWrap: "wrap",
            }}>
              <StatusBadge status={quote.status} label={t(`jobDetail.quoteStatus.${quote.status}`, quote.status)} />
              <span style={{
                fontFamily: "var(--font-mono)", fontWeight: "var(--weight-semibold)",
                fontSize: "var(--text-lg)", color: "var(--text-body)",
              }}>{money(quote.total_due, "$0.00")}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/**
 * Grounding sources. Rendered small and last: they are the receipt for an answer
 * the client already read, not the answer.
 */
function KnowledgeSources({ data }) {
  const { t } = useTranslation();
  const passages = (data?.passages || []).filter((p) => p.url_path);
  if (!passages.length) return null;
  const seen = new Set();
  const links = passages.filter((p) => {
    if (seen.has(p.url_path)) return false;
    seen.add(p.url_path);
    return true;
  }).slice(0, 4);
  return (
    <div>
      <p style={eyebrow}>
        <Icon name="file-text" size={12} style={{ color: "var(--dusk)" }} />
        {t("dany.widgets.sourcesCaption")}
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
        {links.map((p) => (
          <Link key={p.url_path} to={p.url_path} style={{ textDecoration: "none" }}>
            <Chip icon="arrow-right">{p.title}</Chip>
          </Link>
        ))}
      </div>
    </div>
  );
}

const eyebrow = {
  display: "flex", alignItems: "center", gap: 6, marginBottom: "var(--space-3)",
  fontFamily: "var(--font-display)", fontSize: "var(--text-xs)",
  textTransform: "uppercase", letterSpacing: "var(--tracking-widest)",
  color: "var(--dany-meta)",
};

const seeAll = {
  marginTop: "var(--space-3)", display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "var(--font-display)", fontSize: "var(--text-sm)",
  fontWeight: "var(--weight-semibold)", color: "var(--text-accent)", textDecoration: "none",
};

/**
 * The receipt for a write that already happened (DY2.1).
 *
 * `publish_job` is not a tool the model can call — it is the name the execute
 * endpoint writes into the transcript when a job goes live, so that a reloaded
 * conversation shows "your job is live" where it happened rather than a draft
 * card offering to publish something that is already published.
 */
function PublishedJob({ data }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const job = data?.job;
  if (!job) return null;
  const place = [job.location?.city, job.location?.state].filter(Boolean).join(", ");
  return (
    <Card variant="raised" padding="md" data-testid="dany-job-published-replay">
      <p style={{
        fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
        fontSize: "var(--text-lg)", color: "var(--text-body)",
      }}>{t("dany.job.publishedTitle")}</p>
      <p style={{
        marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
        color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)",
      }}>
        {place
          ? t("dany.job.publishedBody", { category: job.category_id || "", place })
          : t("dany.job.publishedBodyNoPlace", { category: job.category_id || "" })}
      </p>
      <Button variant="secondary" size="sm" iconRight="arrow-right"
              style={{ marginTop: "var(--space-4)" }}
              onClick={() => navigate(jobDetailPath(user?.profile?.role, job.job_id))}>
        {t("dany.job.viewJob")}
      </Button>
    </Card>
  );
}

/**
 * The open board, as a FixyBee sees it (DY2.2).
 *
 * Not `JobResults`: that one is the caller's OWN jobs and its button opens
 * their own namespace. This one is other people's work, so it goes to the
 * public job page, where the Send a quote button already lives for anyone who
 * would rather leave the conversation. Since DY2.6 the distinction is sharper
 * than it looks: a FixyBee can appear on both sides, and these two widgets are
 * how the screen keeps "work I need done" apart from "work I could take".
 */
function OpenJobResults({ data }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const jobs = (data?.jobs || []).slice(0, 3);
  if (!jobs.length) return null;
  return (
    <div>
      <p style={eyebrow}>
        <Icon name="briefcase" size={12} style={{ color: "var(--hive-ink)" }} />
        {t("dany.widgets.openJobsCaption", { count: jobs.length })}
      </p>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
        gap: "var(--space-3)",
      }}>
        {jobs.map((job) => (
          <JobCard
            key={job.job_id}
            title={job.title}
            category={job.category_id}
            city={job.location?.city}
            state={job.location?.state}
            locationLabel={jobLocationText(job)}
            budgetLabel={budgetText(t, job.budget_min, job.budget_max)}
            quotesCount={job.quotes_count}
            status={job.status}
            ctaLabel={t("dany.widgets.openJob")}
            onAction={() => navigate(`/provider/jobs/${job.job_id}`)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * What the recommender made of a FixyBee's own words (DY2.2).
 *
 * The matches are shown so the person can see what was understood before the
 * setup card opens with them ticked. The *proposal* — a category the catalog
 * does not have yet — carries its own button, because adding a row to the
 * catalog is a write and Dany does not do writes: `POST /ai/categories/confirm`
 * is the endpoint the classic onboarding form already uses for exactly this.
 */
function SuggestedCategories({ data }) {
  const { t } = useTranslation();
  const [proposal, setProposal] = React.useState(data?.proposal || null);
  const [added, setAdded] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const matches = data?.matches || [];
  if (!matches.length && !proposal && !added) return null;

  const confirm = async () => {
    setBusy(true);
    try {
      const { data: answer } = await api.post("/ai/categories/confirm", proposal);
      setAdded(answer.category);
      setProposal(null);
    } catch {
      setBusy(false);
    }
  };

  return (
    <div>
      <p style={eyebrow}>
        <Icon name="list" size={12} style={{ color: "var(--hive-ink)" }} />
        {t("dany.widgets.specialtiesCaption")}
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
        {matches.map((match) => (
          <Chip key={match.category_id} icon="check">
            {categoryLabel(t, match)}
          </Chip>
        ))}
        {added && (
          <Chip key={added.category_id} icon="check">
            {categoryLabel(t, added)}
          </Chip>
        )}
      </div>
      {proposal && (
        <div style={{ marginTop: "var(--space-3)" }}
             data-testid="dany-category-proposal">
          <p style={{
            fontSize: "var(--text-sm)", color: "var(--text-muted)",
            lineHeight: "var(--leading-relaxed)",
          }}>
            {t("dany.widgets.newCategoryBody", { name: categoryLabel(t, proposal) })}
          </p>
          <Button variant="secondary" size="sm" icon="plus" disabled={busy}
                  style={{ marginTop: "var(--space-2)" }} onClick={confirm}
                  data-testid="dany-category-confirm">
            {t("dany.widgets.addNewCategory", { name: categoryLabel(t, proposal) })}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * The sent-quote receipt (DY2.2), for the same reason PublishedJob exists: a
 * reloaded conversation must show "it went" where it went, not a Send button
 * over a quote that is already with the client.
 */
function SentQuote({ data }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const quote = data?.quote;
  if (!quote) return null;
  return (
    <Card variant="raised" padding="md" data-testid="dany-quote-sent-replay">
      <p style={{
        fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
        fontSize: "var(--text-lg)", color: "var(--text-body)",
      }}>{t("dany.quote.sentTitle")}</p>
      <p style={{
        marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
        color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)",
      }}>
        {t("dany.quote.sentBody", {
          total: money(quote.total_due, "$0.00").replace("$", ""),
          job: data?.job_title || "",
        })}
      </p>
      <Button variant="secondary" size="sm" iconRight="arrow-right"
              style={{ marginTop: "var(--space-4)" }}
              onClick={() => navigate("/provider/quotes")}>
        {t("dany.quote.viewQuotes")}
      </Button>
    </Card>
  );
}

/** The sent-request receipt, for the same reason PublishedJob exists. */
function SentQuoteRequest({ data }) {
  const { t } = useTranslation();
  if (!data?.sent) return null;
  return (
    <Card variant="raised" padding="md" data-testid="dany-quote-request-replay">
      <p style={{
        fontFamily: "var(--font-display)", fontWeight: "var(--weight-bold)",
        fontSize: "var(--text-lg)", color: "var(--text-body)",
      }}>
        {t("dany.quoteRequest.sentTitle", {
          name: data?.request?.provider_name || "",
        })}
      </p>
      <p style={{
        marginTop: "var(--space-2)", fontSize: "var(--text-sm)",
        color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)",
      }}>{t("dany.quoteRequest.sentBody")}</p>
    </Card>
  );
}

/**
 * T25: the client's door to the FixyBee side, on this same account. Only a
 * link — `/signup/provider` sees the session and asks for the phone and trade.
 */
function BecomeFixyBeeLink({ data }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!data?.url) return null;
  return (
    <Button variant="secondary" size="sm" iconRight="arrow-right"
            onClick={() => navigate(data.url)} data-testid="dany-become-fixybee">
      {t("signupX.offerServices")}
    </Button>
  );
}

const BY_TOOL = {
  find_fixybees: FixyBeeResults,
  browse_catalog: CategoryResults,
  get_plans: PlanResults,
  my_jobs: JobResults,
  my_quotes: QuoteResults,
  search_knowledge: KnowledgeSources,
  propose_job: DanyJobDraftWidget,
  request_quote: DanyQuoteRequestWidget,
  publish_job: PublishedJob,
  // N9 (e). The same component as the draft, told apart by `data.discarded`,
  // for the reason update_profile gives below: the card that says a draft is
  // gone is a state of that draft, not a different object.
  discard_job_draft: DanyJobDraftWidget,
  // Both halves of its lifecycle — the offer and the receipt — are the same
  // component, told apart by `data.saved`, because the fields it shows are the
  // fields it saved and rendering them twice differently would be two truths.
  update_profile: DanyProfileWidget,
  add_job_photos: DanyJobPhotosWidget,
  // DY2.2 — the FixyBee's side.
  open_jobs: OpenJobResults,
  suggest_service_categories: SuggestedCategories,
  propose_quote: DanyQuoteWidget,
  send_quote: SentQuote,
  // Both halves of its lifecycle are the same component, told apart by
  // `data.saved` — the same argument update_profile makes above.
  provider_setup: DanyProviderSetupWidget,
  // The only card with no button: DANY.md §2.8 forbids auto-filling the amount,
  // so a "use this price" control is precisely what it must not offer.
  search_market_prices: DanyMarketPriceWidget,
  // DY2.4 — human escalation. Both halves of its lifecycle are the same
  // component, told apart by `data.sent`, on the same argument update_profile
  // makes: the summary the card shows is the summary it filed, and rendering
  // them as two different things would be two truths about one case.
  open_ticket: DanyTicketWidget,
  become_fixybee: BecomeFixyBeeLink,
};

/**
 * One tool result as a widget, or nothing.
 *
 * A denied or failed tool renders nothing on purpose: the model was told plainly
 * what happened and says so in its own words, which is a better message than a
 * red box the client cannot act on.
 */
export default function DanyWidget({
  tool, data, onAskCategory, threadId, onCommitted,
}) {
  if (!tool || !data || data.denied || data.error) return null;
  // Both write tools write their receipt into the transcript under the SAME
  // tool name the execute endpoint used, so a replayed `request_quote` row can
  // be either the confirmation card or the receipt. `sent` is what tells them
  // apart, and reading it here keeps the widget itself from having to guess
  // which half of its own lifecycle it is in.
  if (tool === "request_quote" && data.sent) {
    return <SentQuoteRequest data={data} />;
  }
  const Component = BY_TOOL[tool];
  if (!Component) return null;
  return (
    <Component data={data} onAskCategory={onAskCategory} threadId={threadId}
               onCommitted={onCommitted} />
  );
}
